import * as d3 from 'd3';
import React, { useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  useTheme,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { CostCategory } from 'services/cost-breakdown-calculator';

const useStyles = makeStyles((theme: any) => ({
  card: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(2),
    boxShadow: theme.shadows[1],
  },
  cardContent: {
    padding: theme.spacing(2),
    '&:last-child': {
      paddingBottom: theme.spacing(2),
    },
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(2),
    letterSpacing: '0.5px',
  },
  container: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  chartContainer: {
    width: '100%',
    height: 'auto',
    display: 'flex',
    justifyContent: 'center',
    marginBottom: theme.spacing(2),
    '& svg': {
      maxWidth: '100%',
      height: 'auto',
    },
  },
  legendContainer: {
    width: '100%',
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    justifyContent: 'center',
    marginTop: theme.spacing(2),
    padding: theme.spacing(1),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.spacing(0.5),
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  legendColor: {
    width: 16,
    height: 16,
    borderRadius: 2,
  },
  legendLabel: {
    fontSize: '13px',
    fontWeight: 500,
    color: theme.palette.text.primary,
  },
  tooltip: {
    position: 'absolute',
    padding: theme.spacing(1),
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    color: '#fff',
    borderRadius: theme.spacing(0.5),
    pointerEvents: 'none',
    fontSize: '12px',
    zIndex: 1000,
    maxWidth: '200px',
    wordWrap: 'break-word',
    display: 'none',
    '&.show': {
      display: 'block',
    },
  },
}));

interface CostBreakdownStackedBarProps {
  buyerCosts: CostCategory[];
  sellerCosts: CostCategory[];
}

/**
 * CostBreakdownStackedBar - D3-based stacked bar chart component
 *
 * Displays buyer vs seller cost comparison as two vertical stacked bars.
 * Each bar is segmented by cost category with distinct colors.
 *
 * Features:
 * - Two bars side-by-side (Buyer left, Seller right)
 * - Stacked by category with Material-UI theme colors
 * - Responsive design with container-based sizing
 * - Hover tooltips showing category, amount, and percentage
 * - Bilingual labels via i18n
 * - Legend showing category → color mapping
 */
const CostBreakdownStackedBar: React.FC<CostBreakdownStackedBarProps> = ({
  buyerCosts = [],
  sellerCosts = [],
}) => {
  const classes = useStyles();
  const { t } = useTranslation('closingDisclosure');
  const theme = useTheme();
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const getCategoryColor = useCallback((categoryKey: string): string => {
    const colorMap: Record<string, string> = {
      earnest: theme.palette.primary.main,
      commission: theme.palette.secondary.main,
      title: theme.palette.success.main,
      escrow: theme.palette.warning.main,
      taxes: theme.palette.error.main,
      other: theme.palette.info.main,
    };
    return colorMap[categoryKey] || theme.palette.info.main;
  }, [theme]);

  const formatCurrency = useCallback((cents: number): string => {
    const dollars = cents / 100;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(dollars);
  }, []);

  useEffect(() => {
    if (!svgRef.current || !containerRef.current) return;

    if (buyerCosts.length === 0 && sellerCosts.length === 0) return;

    const allCategories = Array.from(
      new Set([...buyerCosts, ...sellerCosts].map((c) => c.category_key))
    );

    const buyerTotal = buyerCosts.reduce((sum, c) => sum + c.amount, 0);
    const sellerTotal = sellerCosts.reduce((sum, c) => sum + c.amount, 0);

    const data = [
      {
        name: t('roles-buyer'),
        categories: allCategories.map((cat) => {
          const cost = buyerCosts.find((c) => c.category_key === cat);
          return {
            category: cat,
            amount: cost?.amount || 0,
            label: cost?.label || cat,
          };
        }),
        total: buyerTotal,
      },
      {
        name: t('roles-seller'),
        categories: allCategories.map((cat) => {
          const cost = sellerCosts.find((c) => c.category_key === cat);
          return {
            category: cat,
            amount: cost?.amount || 0,
            label: cost?.label || cat,
          };
        }),
        total: sellerTotal,
      },
    ];

    d3.select(svgRef.current).selectAll('*').remove();

    const containerWidth = containerRef.current.offsetWidth;
    const width = Math.min(containerWidth, 600);
    const height = 267;
    const margin = { top: 20, right: 40, bottom: 60, left: 50 };
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    const maxValue = Math.max(buyerTotal, sellerTotal);

    const yScale = d3
      .scaleLinear()
      .domain([0, maxValue])
      .range([innerHeight, 0]);

    const xScale = d3
      .scaleBand()
      .domain(data.map((d) => d.name))
      .range([0, innerWidth])
      .padding(0.4);

    const svg = d3
      .select(svgRef.current)
      .attr('viewBox', [0, 0, width, height])
      .attr('width', width)
      .attr('height', height)
      .attr('style', 'max-width: 100%; height: auto;');

    const g = svg
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    g.append('g')
      .call(d3.axisLeft(yScale).tickFormat((d) => formatCurrency(d as number)))
      .style('font-size', '12px');

    g.append('g')
      .attr('transform', `translate(0,${innerHeight})`)
      .call(d3.axisBottom(xScale))
      .style('font-size', '12px');

    const tooltip = d3
      .select(tooltipRef.current);

    const barGroups = g
      .selectAll('.bar-group')
      .data(data)
      .join('g')
      .attr('class', 'bar-group')
      .attr('transform', (d) => `translate(${xScale(d.name)},0)`);

    g.selectAll('.bar-group').remove();

    data.forEach((roleData, roleIndex) => {
      const barGroup = g
        .append('g')
        .attr('transform', `translate(${xScale(roleData.name)},0)`);

      let cumulativeAmount = 0;

      allCategories.forEach((category) => {
        const categoryData = roleData.categories.find((c) => c.category === category);
        if (categoryData && categoryData.amount > 0) {
          const segmentTopAmount = cumulativeAmount + categoryData.amount;
          const yPos = yScale(segmentTopAmount);
          const segmentHeight = yScale(cumulativeAmount) - yScale(segmentTopAmount);

          barGroup
            .append('rect')
            .attr('x', 0)
            .attr('y', yPos)
            .attr('width', xScale.bandwidth())
            .attr('height', segmentHeight)
            .attr('fill', getCategoryColor(categoryData.category))
            .attr('opacity', 0.9)
            .attr('class', `segment-${categoryData.category}`)
            .on('mouseover', function onMouseOver(this: SVGElement) {
              d3.select(this).attr('opacity', 1);

              const percentage = roleData.total > 0
                ? ((categoryData.amount / roleData.total) * 100).toFixed(1)
                : '0.0';

              tooltip
                .style('display', 'block')
                .html(
                  `<strong>${categoryData.label}</strong><br/>
                    ${formatCurrency(categoryData.amount)}<br/>
                    ${percentage}% of total`
                );
            })
          // eslint-disable-next-line prefer-arrow-callback
            .on('mousemove', function onMouseMove(this: SVGElement, event: MouseEvent) {
              tooltip
                .style('left', `${event.pageX + 10}px`)
                .style('top', `${event.pageY - 10}px`);
            })
            .on('mouseout', function onMouseOut(this: SVGElement) {
              d3.select(this).attr('opacity', 0.9);
              tooltip.style('display', 'none');
            });

          // Add amount label on segment if large enough
          if (segmentHeight > 30) {
            barGroup
              .append('text')
              .attr('x', xScale.bandwidth() / 2)
              .attr('y', yPos + segmentHeight / 2)
              .attr('text-anchor', 'middle')
              .attr('dy', '0.35em')
              .attr('fill', '#fff')
              .attr('font-size', '11px')
              .attr('font-weight', 'bold')
              .attr('pointer-events', 'none')
              .text(formatCurrency(categoryData.amount));
          }

          cumulativeAmount += categoryData.amount;
        }
      });

      barGroup
        .append('text')
        .attr('x', xScale.bandwidth() / 2)
        .attr('y', innerHeight + 40)
        .attr('text-anchor', 'middle')
        .attr('fill', theme.palette.text.primary)
        .attr('font-size', '12px')
        .attr('font-weight', 'bold')
        .text(`${t('chart-total')}: ${formatCurrency(roleData.total)}`);
    });
  }, [buyerCosts, sellerCosts, t, theme, getCategoryColor, formatCurrency]);

  const allCategories = Array.from(
    new Set([...buyerCosts, ...sellerCosts].map((c) => c.category_key))
  );

  return (
    <Card className={classes.card}>
      <CardContent className={classes.cardContent}>
        <Typography className={classes.sectionTitle}>
          {t('cost-breakdown-stacked-bar-title')}
        </Typography>
        <Box className={classes.container}>
          <div className={classes.chartContainer} ref={containerRef}>
            <svg ref={svgRef} />
          </div>

          <Box className={classes.legendContainer}>
            {allCategories.map((category) => (
              <Box key={category} className={classes.legendItem}>
                <Box
                  className={classes.legendColor}
                  style={{ backgroundColor: getCategoryColor(category) }}
                />
                <span className={classes.legendLabel}>
                  {t(`categories-${category}`)}
                </span>
              </Box>
            ))}
          </Box>

          <div
            ref={tooltipRef}
            className={classes.tooltip}
            style={{
              position: 'fixed',
            }}
          />
        </Box>
      </CardContent>
    </Card>
  );
};

export default CostBreakdownStackedBar;
